import { lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RideDetailPage } from '@/pages/RideDetailPage';
import { SearchPage } from '@/pages/SearchPage';

// Signed-in screens are split into their own chunks: an anonymous visitor searching for a
// ride never downloads the admin portal, the dashboards or the messaging UI.
const AdminPage = lazy(() => import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const BookingsPage = lazy(() => import('@/pages/BookingsPage').then((m) => ({ default: m.BookingsPage })));
const CreateRidePage = lazy(() => import('@/pages/CreateRidePage').then((m) => ({ default: m.CreateRidePage })));
const EditRidePage = lazy(() => import('@/pages/EditRidePage').then((m) => ({ default: m.EditRidePage })));
const MessagesPage = lazy(() => import('@/pages/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const MyRidesPage = lazy(() => import('@/pages/MyRidesPage').then((m) => ({ default: m.MyRidesPage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const PublicProfilePage = lazy(() => import('@/pages/PublicProfilePage').then((m) => ({ default: m.PublicProfilePage })));
const VehiclesPage = lazy(() => import('@/pages/VehiclesPage').then((m) => ({ default: m.VehiclesPage })));

/**
 * The route table. Public: browsing and searching rides, and the auth pages. Everything
 * that reads or writes a person's own data sits behind RequireAuth. The admin route
 * additionally requires the ADMIN role, as a UI courtesy only -- the API enforces it.
 */
export function App() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/rides/:rideId" element={<RideDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/users/:userId" element={<PublicProfilePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/vehicles" element={<VehiclesPage />} />
          <Route path="/create-ride" element={<CreateRidePage />} />
          <Route path="/rides/:rideId/edit" element={<EditRidePage />} />
          <Route path="/my-rides" element={<MyRidesPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>
        <Route element={<RequireAuth admin />}>
          <Route path="/admin" element={<AdminPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
